import React, { useState } from 'react';
import { ArrowLeft } from 'lucide-react';

const ContactUs = () => {
  // Form state
  const [formData, setFormData] = useState({
    email: '',
    subject: '',
    message: ''
  });

  const Backend_URL = process.env.REACT_APP_BACKEND_URL;
  
  // Status states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({
    success: false,
    error: false,
    message: ''
  });

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ success: false, error: false, message: '' });
    
    try {
      const response = await fetch(`${Backend_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (!response.ok) {
        throw new Error('Failed to send message');
      }
      
      const data = await response.json();
      
      // Reset form on success
      setFormData({ email: '', subject: '', message: '' });
      setSubmitStatus({
        success: true,
        error: false,
        message: 'Your message has been sent successfully!'
      });
    } catch (error) {
      setSubmitStatus({
        success: false,
        error: true,
        message: error.message || 'Something went wrong. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact-us" className="py-16 bg-gray-100">
      <div className="max-w-4xl mx-auto px-4">
        {/* Back button */}
        <div className="mb-8">
          <a 
            href="/" 
            className="inline-flex items-center text-gray-700 hover:text-black transition-colors"
          >
            <ArrowLeft size={20} className="mr-2" />
            <span>Back to Home</span>
          </a>
        </div>

        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold mb-4">Contact Us</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Have questions or feedback? We'd love to hear from you. Fill out the form below and we'll get back to you as soon as possible.
          </p>
        </div>
        
        <div className="bg-white rounded-lg shadow-lg p-8">
          {submitStatus.success ? (
            <div className="text-center py-8">
              <div className="text-green-500 text-6xl mb-4">✓</div>
              <h3 className="text-xl font-bold mb-2">Thank You!</h3>
              <p className="text-gray-600 mb-6">{submitStatus.message}</p>
              <button 
                onClick={() => setSubmitStatus({ success: false, error: false, message: '' })}
                className="bg-black text-white px-6 py-2 rounded-full hover:bg-gray-800 transition"
              >
                Send Another Message
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email Field */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="your@email.com"
                />
              </div>
              
              {/* Subject Field */}
              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                  Subject
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="How can we help you?"
                />
              </div>
              
              {/* Message Field */}
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="5"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  placeholder="Please provide details about your inquiry..."
                />
              </div>
              
              {/* Error Message */}
              {submitStatus.error && (
                <div className="text-red-500 text-sm py-2">
                  {submitStatus.message}
                </div>
              )}
              
              {/* Submit Button */}
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full bg-black text-white font-bold py-3 px-6 rounded-md hover:bg-gray-800 transition ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default ContactUs;